import { create } from 'zustand'

export type SelectionView = 'map' | 'graph' | 'timeline'
export type SelectionMode = 'single' | 'multi'

interface SelectionState {
  /**
   * Canonical entity IDs that are selected. Cross-view: any view that knows an
   * entity ID can mark it selected here, and every other view will react.
   *
   * Selection MUST always render in views — even when an entity would otherwise
   * be hidden by the view's filter — with an "outside filter" indicator. This
   * is the cross-view glue: filters are per-view objectives, selection follows
   * the user across views.
   */
  selectedEntityIds: Set<string>

  /**
   * Canonical relationship (edge) selection. At most one edge is selected at a
   * time — selecting an edge replaces any previous edge selection. Used by the
   * graph sidebar to auto-expand and focus the matching row in the Relationships
   * section when a user clicks an edge on the canvas.
   */
  selectedEdgeId: string | null

  selectionMode: SelectionMode

  /** Tracks where the most recent change came from, for telemetry / UI hints. */
  lastInteractionView: SelectionView | null

  /**
   * Monotonic counter bumped by `focusEntity`. Views distinguish "the user
   * asked to be taken to this entity" from "a selection was restored" — the
   * former moves the camera, the latter must not (restoring a selection on
   * mount should never jerk the viewport). Views compare against the last
   * value they acted on rather than reading it as state.
   */
  focusNonce: number

  /** Replace the entire selection with the given IDs. */
  replace(ids: Iterable<string>, origin?: SelectionView): void

  /**
   * Select a single entity AND ask every view to bring it into view. This is
   * the channel for "take me to this thing" affordances that live outside the
   * canvas — chat tool results, sidebar rows, search hits.
   */
  focusEntity(id: string, origin?: SelectionView): void

  /** Add IDs to the current selection. */
  add(ids: Iterable<string>, origin?: SelectionView): void

  /** Remove IDs from the current selection. */
  remove(ids: Iterable<string>, origin?: SelectionView): void

  /** Toggle a single ID in the selection. */
  toggle(id: string, origin?: SelectionView): void

  /** Clear all selection (entities + edge). */
  clear(origin?: SelectionView): void

  /** Replace the currently selected edge (pass null to clear). */
  selectEdge(edgeId: string | null, origin?: SelectionView): void

  setMode(mode: SelectionMode): void
}

export const useSelectionStore = create<SelectionState>()(set => ({
  selectedEntityIds: new Set<string>(),
  selectedEdgeId: null,
  selectionMode: 'multi',
  lastInteractionView: null,
  focusNonce: 0,

  replace(ids, origin) {
    const next = new Set(ids)
    set(state =>
      eqSet(state.selectedEntityIds, next)
        ? state
        : { selectedEntityIds: next, lastInteractionView: origin ?? state.lastInteractionView }
    )
  },

  focusEntity(id, origin) {
    // Always bumps the nonce, even when `id` is already the selection — the
    // user clicking "focus" a second time is a fresh request to be taken
    // there, not a no-op.
    set(state => ({
      selectedEntityIds: new Set([id]),
      lastInteractionView: origin ?? state.lastInteractionView,
      focusNonce: state.focusNonce + 1,
    }))
  },

  add(ids, origin) {
    set(state => {
      const next = new Set(state.selectedEntityIds)
      let changed = false
      for (const id of ids) {
        if (!next.has(id)) {
          next.add(id)
          changed = true
        }
      }
      if (!changed) return state
      return { selectedEntityIds: next, lastInteractionView: origin ?? state.lastInteractionView }
    })
  },

  remove(ids, origin) {
    set(state => {
      const next = new Set(state.selectedEntityIds)
      let changed = false
      for (const id of ids) {
        if (next.delete(id)) changed = true
      }
      if (!changed) return state
      return { selectedEntityIds: next, lastInteractionView: origin ?? state.lastInteractionView }
    })
  },

  toggle(id, origin) {
    set(state => {
      const next = new Set(state.selectedEntityIds)
      if (!next.delete(id)) next.add(id)
      return { selectedEntityIds: next, lastInteractionView: origin ?? state.lastInteractionView }
    })
  },

  clear(origin) {
    set(state =>
      state.selectedEntityIds.size === 0 && state.selectedEdgeId === null
        ? state
        : {
            selectedEntityIds: new Set<string>(),
            selectedEdgeId: null,
            lastInteractionView: origin ?? state.lastInteractionView,
          }
    )
  },

  selectEdge(edgeId, origin) {
    set(state =>
      state.selectedEdgeId === edgeId
        ? state
        : { selectedEdgeId: edgeId, lastInteractionView: origin ?? state.lastInteractionView }
    )
  },

  setMode(mode) {
    set(state => (state.selectionMode === mode ? state : { selectionMode: mode }))
  },
}))

/**
 * Subscribe to whether a single entity is selected. Avoids re-rendering on
 * unrelated selection changes.
 */
export function useIsEntitySelected(id: string | null | undefined): boolean {
  return useSelectionStore(s => (id ? s.selectedEntityIds.has(id) : false))
}

/** Number of selected entities. Subscribes to size only. */
export function useSelectionSize(): number {
  return useSelectionStore(s => s.selectedEntityIds.size)
}

/** Subscribe to whether the given edge id is the currently selected edge. */
export function useIsEdgeSelected(id: string | null | undefined): boolean {
  return useSelectionStore(s => (id ? s.selectedEdgeId === id : false))
}

function eqSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) if (!b.has(id)) return false
  return true
}
